/**
 * Error Boundary Component for Advisor Panel
 * Graceful error handling with recovery options
 */

import { Component, type ReactNode, type ErrorInfo } from "react";
import { AlertTriangle, RefreshCw, LogOut, Bug } from "lucide-react";
import { Button } from "./Button";
import { cn } from "@/shared/lib/utils";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show detailed error info (dev only) */
  showDetails?: boolean;
  /** Custom reset handler */
  onReset?: () => void;
  /** Variant for different contexts */
  variant?: "page" | "section" | "inline";
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showErrorDetails: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false,
    });
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  handleLogout = () => {
    // Clear auth state and redirect to login
    localStorage.removeItem("advisor_auth_token");
    localStorage.removeItem("advisor_panel_admin_token");
    localStorage.removeItem("advisor-storage");
    localStorage.removeItem("admin-storage");
    window.location.href = "/login";
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showErrorDetails: !prev.showErrorDetails }));
  };

  render() {
    const { hasError, error, errorInfo, showErrorDetails } = this.state;
    const { children, fallback, showDetails = import.meta.env.DEV, variant = "page" } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Variant-specific styling
    const containerStyles = {
      page: "min-h-screen flex items-center justify-center p-4 bg-gray-50",
      section:
        "min-h-[300px] flex items-center justify-center p-4 rounded-lg border border-gray-200 bg-white",
      inline: "p-4 rounded-lg border border-red-200 bg-red-50",
    };

    const cardStyles = {
      page: "max-w-md w-full p-6 rounded-lg border border-gray-200 bg-white shadow-lg",
      section: "max-w-sm w-full text-center",
      inline: "text-sm",
    };

    return (
      <div className={containerStyles[variant]}>
        <div className={cardStyles[variant]}>
          <div className={cn("flex items-center gap-3", variant === "inline" ? "" : "mb-4")}>
            <div
              className={cn(
                "flex items-center justify-center rounded-full bg-red-100",
                variant === "inline" ? "h-8 w-8" : "h-12 w-12"
              )}
            >
              <AlertTriangle
                className={cn("text-red-600", variant === "inline" ? "h-4 w-4" : "h-6 w-6")}
                aria-hidden="true"
              />
            </div>
            <div>
              <h2
                className={cn("font-semibold text-gray-900", variant === "inline" ? "text-sm" : "text-lg")}
              >
                {variant === "inline" ? "Błąd" : "Wystąpił błąd"}
              </h2>
              {variant !== "inline" && (
                <p className="text-sm text-gray-500">
                  Przepraszamy za niedogodności.
                </p>
              )}
            </div>
          </div>

          {variant !== "inline" && (
            <>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600">
                  Wystąpił nieoczekiwany błąd w panelu doradcy.
                  Możesz spróbować odświeżyć stronę lub zalogować się ponownie.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={this.handleReset} size="sm" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Spróbuj ponownie
                </Button>
                <Button onClick={this.handleLogout} variant="outline" size="sm" className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Wyloguj się
                </Button>
                {showDetails && (
                  <Button
                    onClick={this.toggleDetails}
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                  >
                    <Bug className="h-4 w-4" />
                    {showErrorDetails ? "Ukryj" : "Szczegóły"}
                  </Button>
                )}
              </div>

              {showDetails && showErrorDetails && error && (
                <div className="mt-4 p-3 rounded-lg bg-gray-100 border border-gray-200 overflow-auto max-h-48">
                  <p className="text-xs font-mono text-red-600 mb-2">{error.toString()}</p>
                  {errorInfo && (
                    <pre className="text-xs font-mono text-gray-500 whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}
            </>
          )}

          {variant === "inline" && (
            <div className="mt-2 flex gap-2">
              <Button onClick={this.handleReset} variant="outline" size="sm">
                Spróbuj ponownie
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

/**
 * Section-level error boundary for isolating failures
 */
export function SectionErrorBoundary({
  children,
  title,
  onRetry,
}: {
  children: ReactNode;
  title?: string;
  onRetry?: () => void;
}) {
  return (
    <ErrorBoundary
      variant="section"
      onReset={onRetry}
      fallback={
        <div className="min-h-[200px] flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 bg-white text-center">
          <AlertTriangle className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">
            {title ? `Nie udało się załadować: ${title}` : "Wystąpił błąd w tej sekcji"}
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Reszta panelu działa poprawnie.
          </p>
          <Button onClick={() => window.location.reload()} variant="outline" size="sm">
            Odśwież stronę
          </Button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
